const { getCookie, setCookie } = window.BAO.Utils

class SaleModal extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.defaultProperties = {
      'Accepts Marketing': 'true',
    }

    this.checkState()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    if (this.els?.close?.exists) {
      this.listeners.add(this.els.close.element, 'click', () => {
        this.visible = false
      })
    }

    this.listeners.add(this.els.button.element, 'click', () => {
      localStorage.removeItem('single_show_thankyou')
    })

    this.listeners.add(this.els.copy.element, 'click', this.copyCode.bind(this))
  }

  copyCode () {
    const code = this.els.copyMessage.element.innerText
    const success = this.els.copy.element.dataset.copyTextSuccess

    navigator.clipboard.writeText(code).then(() => {
      this.els.copyMessage.element.innerText = success

      setTimeout(() => {
        this.els.copyMessage.element.innerText = code
      }, 2000)
    })
  }

  checkState () {
    if (localStorage.getItem('single_show_thankyou') === 'true') {
      this.toggleContent('thankyou')
      this.visible = true
      return
    }

    if (this.params.has(this.dataset.param)) {
      this.toggleContent('subscribed')
      this.visible = false // Remove to ensure modal is hidden on load

      if (!this.isExistingCustomer) {
        this.setExistingCustomerStorage()
      }
    } else if (!this.isExistingCustomer && !this.isNewCustomer) {
      this.visible = true
    } else {
      this.visible = false
    }
  }

  async onFormSubmit (e) {
    e.preventDefault()

    this.toggleErrorMessage()

    if (!window.theme?.klaviyo) return

    const listId = this?.els?.list?.element?.value
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    if (!this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', true)
      this.els.email.element.focus()
      return
    }
    this.els.email.element.removeAttribute('aria-invalid')

    // Check if profile exists already before submitting.
    const profileExists = await this.checkProfileExists(emailAddress)

    // If profile already exists, skip to register form.
    if (profileExists) {
      this.log('Profile already exists')

      this.toggleContent('subscribed')

      this.setExistingCustomerStorage()
      return
    }

    this.createClientSubscription({
      emailAddress,
      listId,
    })
  }

  async createClientSubscription (formData) {
    const { emailAddress, listId } = formData
    const properties = { ...this.defaultProperties }

    const attributes = {
      email: emailAddress,
      properties,
    }

    const bodyData = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.tag,
          profile: {
            data: {
              type: 'profile',
              attributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
        options,
      )

      // Expected success code is 202, but the response body is empty.
      // This is expected according to the docs:
      // 'The request has been received but not yet acted upon. We return this status
      //  code for requests that were accepted but are processed asynchronously.'
      if (response.status === 202) {
        this.log('202')

        this.toggleContent('thankyou')

        this.setNewCustomerStorage()
        this.setExistingCustomerStorage()
        this.checkSaleNavigation()
      } else {
        // There is data returned when there is an error.
        // On success, the response body is empty.
        const jsonResponse = await response.json()

        if (jsonResponse.errors.length > 0) {
          // eslint-disable-next-line no-console
          const errorMessage = jsonResponse.errors[0].detail
            ? jsonResponse.errors[0].detail
            : window.theme.strings.newsletterGenericError
          this.log(errorMessage)

          if (errorMessage) this.toggleErrorMessage(errorMessage)
        }
      }
    } catch (error) {
      const errorMessage =
        error && error.detail
          ? error.detail
          : window.theme.strings.newsletterGenericError
      this.log(errorMessage)

      if (errorMessage) this.toggleErrorMessage(errorMessage)
    }
  }

  async checkProfileExists (emailAddress) {
    const scriptUrl = 'https://self-portrait-klaviyo.vercel.app/api/identify'

    const data = {
      email: emailAddress,
      store: window.theme?.klaviyo?.store,
    }

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    const jsonResponse = await response.json()

    return jsonResponse.registered ? jsonResponse.registered : false
  }

  validEmail () {
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return emailRegex.test(emailAddress)
  }

  toggleErrorMessage (message = false) {
    const errorElement = this.els.error.element
    if (!message) {
      errorElement.setAttribute('aria-hidden', true)
      errorElement.innerHTML = ''
    } else {
      errorElement.setAttribute('aria-hidden', false)
      errorElement.innerHTML = message
    }
  }

  checkSaleNavigation () {
    const headerMenuEl = document.querySelector('header-menu')
    const mobileMenu = document.querySelector('mobile-menu')
    headerMenuEl && headerMenuEl.checkSaleNavigation()
    mobileMenu && mobileMenu.checkSaleNavigation()
  }

  setExistingCustomerStorage () {
    setCookie(
      this.existingCustomerCookieName,
      'true',
      window.theme.saleExistingCustomerCookieExpiration || 7,
    )
  }

  setNewCustomerStorage () {
    setCookie(
      this.newCustomerCookieName,
      'true',
      window.theme.saleNewCustomerCookieExpiration || 1,
    )
  }

  toggleContent (state = 'unsubscribed') {
    // Accepts 'unsubscribed', 'thankyou', 'subscribed'
    if (state === 'subscribed') {
      this.visible = false
      return
    }

    if (this.els.body_unsubscribed.element) {
      this.els.body_unsubscribed.element.ariaHidden = state !== 'unsubscribed'
    }
    if (this.els.body_thankyou.element) {
      this.els.body_thankyou.element.ariaHidden = !state || state !== 'thankyou'
    }
  }

  get isExistingCustomer () {
    return getCookie(this.existingCustomerCookieName)
  }

  get isNewCustomer () {
    return getCookie(this.newCustomerCookieName)
  }

  get existingCustomerCookieName () {
    return (
      window.theme.saleExistingCustomerCookie || `BAO-md-2025-existingCustomer`
    )
  }

  get newCustomerCookieName () {
    return window.theme.saleNewCustomerCookie || `BAO-md-2025-newCustomer`
  }

  get params () {
    return new URLSearchParams(window.location.search)
  }

  get visible () {
    return this.getAttribute('aria-hidden') === 'false'
  }

  set visible (state) {
    this.setAttribute('aria-hidden', !state)
    document.body.classList.toggle('util-SiteOverflowed', state)
  }
}

if (!customElements.get('sale-modal')) {
  customElements.define('sale-modal', SaleModal)
}
